import { useEffect, useRef, useCallback } from 'react';
import * as PIXI from 'pixi.js';
import { Point } from '../../types/point';

export type JumpTrigger = (pointId: number) => void;

/**
 * ARCHITECTURE
 * ─────────────────────────────────────────────────────────────────
 * dataMapRef   — ALL point data received from the server (color, coords).
 *                Never shrunk except when a point is deleted.
 *
 * gridRef      — spatial index over dataMapRef: bucket key → set of ids,
 *                one bucket per BUCKET×BUCKET cells.  Everything that needs
 *                "which points are in this rectangle" goes through it, so no
 *                hot path scans the whole world.  Maintained by gridAdd /
 *                gridMove / gridDrop, which are the ONLY writers.
 *
 * Two draw modes, picked by how many points fall inside the viewport:
 *
 *   sprite mode (few points)  — one PIXI.Sprite per visible point, as before.
 *                               Exact 1-cell squares, per-point tint, jump
 *                               animation straight on the sprite.
 *
 *   bitmap mode (many points) — every point is a texel in ONE texture drawn by
 *                               ONE sprite.  85k sprites cost 85k transform +
 *                               batch updates per frame; a texture costs one.
 *                               The texture lives in world space (texel =
 *                               `cell` world cells, ~1 screen pixel) and is
 *                               rebuilt only when the camera leaves the cached
 *                               region or the data changes — panning inside the
 *                               region is a pure transform.
 *
 * Points in overlayIdsRef (the player's own pixel, anything mid-jump) always
 * get a real sprite and are skipped by the bitmap, so they stay animatable and
 * never show as a doubled ghost.
 *
 * RENDERING IS ON DEMAND.  The PIXI ticker is stopped; `scheduleFrame()` runs
 * reconcile + render once per animation frame and only when something actually
 * changed.  A still world costs nothing.
 *
 * Y-AXIS INVERSION
 * ─────────────────────────────────────────────────────────────────
 * The Go backend uses Y-up ("up" → dy = +1).  The HTML canvas uses Y-down.
 * Each point is placed at (point.x, −point.y) so that higher world-Y appears
 * higher on screen.
 */

// Extra cells to keep rendered beyond the visible edge.
// Prevents sprite pop-in during slow pan; large enough to cover 1-frame lag.
const CULL_PAD = 10;

// Spatial index granularity, in world cells per bucket side.
const BUCKET = 64;

// Sprite ⇄ bitmap switch, with hysteresis so panning across the boundary
// doesn't flip modes every frame.
const LOD_ENTER = 8000;
const LOD_LEAVE = 5000;

// Bitmap region is this much bigger than the viewport, so small pans don't
// force a rebuild. Capped in texels to bound memory and upload cost.
const REGION_PAD  = 1.35;
const MAX_TEXELS  = 4_000_000;

interface PointData { x: number; y: number; color: string; }

function parseColor(color: string | undefined): number {
  const hex = (color ?? '#ffffff').replace('#', '');
  const num = parseInt(hex, 16);
  return Number.isNaN(num) ? 0xffffff : num;
}

/** #RRGGBB → little-endian RGBA word for a Uint32 view over the texel buffer. */
function parseAbgr(color: string | undefined): number {
  const rgb = parseColor(color);
  return 0xff000000 | ((rgb & 0xff) << 16) | (rgb & 0xff00) | ((rgb >> 16) & 0xff);
}

// Bucket coordinates → one number. Offsetting keeps negative worlds unique and
// exact for |bucket| < 32768, i.e. ±2M cells — far beyond any real coordinate.
const bkey = (bx: number, by: number) => (bx + 32768) * 65536 + (by + 32768);
const bucketOf = (v: number) => Math.floor(v / BUCKET);

export const useWorldRenderer = (
  canvasRef: React.RefObject<HTMLDivElement | null>,
  points   : Point[],
  viewport : { x: number; y: number; zoom: number },
  width    : number,
  height   : number,
  opts?    : { preserveDrawingBuffer?: boolean },
): {
  triggerJump: JumpTrigger;
  imperativeMove: (pointId: number, x: number, y: number) => void;
  getCanvas: () => HTMLCanvasElement | null;
} => {
  const appRef       = useRef<PIXI.Application | null>(null);
  const canvasElRef  = useRef<HTMLCanvasElement | null>(null);
  // preserveDrawingBuffer must be known at PIXI init time (read once via ref).
  const preserveRef  = useRef(opts?.preserveDrawingBuffer ?? false);
  preserveRef.current = opts?.preserveDrawingBuffer ?? false;
  const worldRef     = useRef<PIXI.Container   | null>(null);
  // All point data, including off-screen points.
  const dataMapRef   = useRef<Map<number, PointData>>(new Map());
  // Spatial index over dataMapRef — bucket key → ids in that bucket.
  const gridRef      = useRef<Map<number, Set<number>>>(new Map());
  // Sprites: every visible point in sprite mode, only overlay points in bitmap mode.
  const spriteMapRef = useRef<Map<number, PIXI.Sprite>>(new Map());
  // Points that must always be a sprite: the own pixel, anything mid-jump.
  const overlayIdsRef = useRef<Set<number>>(new Set());
  // Current viewport bounds used for culling — updated on every viewport change.
  const viewBoundsRef = useRef({ x: 0, y: 0, zoom: 1, w: 0, h: 0 });
  const modeRef       = useRef<'sprite' | 'bitmap'>('sprite');
  const seenIdsRef    = useRef<Set<number>>(new Set());

  // Jump animation state
  const zoomRef        = useRef(viewport.zoom);
  const jumpOffsetsRef = useRef<Map<number, number>>(new Map());
  const jumpAnimsRef   = useRef<Map<number, { startTime: number; amplitude: number }>>(new Map());
  const animRafRef     = useRef<number | null>(null);

  // Frame scheduling
  const frameRafRef    = useRef<number | null>(null);

  // Bitmap-mode state
  const bmpRef = useRef<{
    sprite : PIXI.Sprite | null;
    base   : PIXI.BaseTexture | null;
    bytes  : Uint8Array | null;
    words  : Uint32Array | null;
    texW   : number;  texH: number;
    cell   : number;             // world cells per texel
    x0     : number;  yTop: number; // world coords of texel (0,0)
    dirty  : boolean;            // point data changed since last build
  }>({
    sprite: null, base: null, bytes: null, words: null,
    texW: 0, texH: 0, cell: 1, x0: 0, yTop: 0, dirty: true,
  });

  // ── Spatial index ──────────────────────────────────────────────
  const gridAdd = (id: number, x: number, y: number) => {
    const k = bkey(bucketOf(x), bucketOf(y));
    let set = gridRef.current.get(k);
    if (!set) { set = new Set(); gridRef.current.set(k, set); }
    set.add(id);
  };

  const gridDrop = (id: number, x: number, y: number) => {
    const k = bkey(bucketOf(x), bucketOf(y));
    const set = gridRef.current.get(k);
    if (!set) return;
    set.delete(id);
    if (set.size === 0) gridRef.current.delete(k);
  };

  const gridMove = (id: number, ox: number, oy: number, nx: number, ny: number) => {
    if (bucketOf(ox) === bucketOf(nx) && bucketOf(oy) === bucketOf(ny)) return;
    gridDrop(id, ox, oy);
    gridAdd(id, nx, ny);
  };

  /** Visit every point inside the world rectangle (inclusive bounds). */
  const forEachInRect = useCallback((
    xMin: number, yMin: number, xMax: number, yMax: number,
    cb: (id: number, d: PointData) => void,
  ) => {
    const grid = gridRef.current;
    const data = dataMapRef.current;
    const bx0 = bucketOf(xMin), bx1 = bucketOf(xMax);
    const by0 = bucketOf(yMin), by1 = bucketOf(yMax);
    for (let bx = bx0; bx <= bx1; bx++) {
      for (let by = by0; by <= by1; by++) {
        const set = grid.get(bkey(bx, by));
        if (!set) continue;
        set.forEach(id => {
          const d = data.get(id);
          if (!d) return;
          if (d.x < xMin || d.x > xMax || d.y < yMin || d.y > yMax) return;
          cb(id, d);
        });
      }
    }
  }, []);

  /** How many points fall inside the rectangle. Buckets fully inside count O(1). */
  const countInRect = useCallback((xMin: number, yMin: number, xMax: number, yMax: number) => {
    const grid = gridRef.current;
    const data = dataMapRef.current;
    const bx0 = bucketOf(xMin), bx1 = bucketOf(xMax);
    const by0 = bucketOf(yMin), by1 = bucketOf(yMax);
    let n = 0;
    for (let bx = bx0; bx <= bx1; bx++) {
      const inX = bx > bx0 && bx < bx1;
      for (let by = by0; by <= by1; by++) {
        const set = grid.get(bkey(bx, by));
        if (!set) continue;
        if (inX && by > by0 && by < by1) { n += set.size; continue; }
        set.forEach(id => {
          const d = data.get(id);
          if (d && d.x >= xMin && d.x <= xMax && d.y >= yMin && d.y <= yMax) n++;
        });
      }
    }
    return n;
  }, []);

  // ── Frame scheduling ───────────────────────────────────────────
  const reconcileRef = useRef<() => void>(() => {});
  const scheduleFrame = useCallback(() => {
    if (frameRafRef.current !== null) return;
    frameRafRef.current = requestAnimationFrame(() => {
      frameRafRef.current = null;
      const app = appRef.current;
      if (!app) return;
      reconcileRef.current();
      try { app.render(); } catch (_) { /* renderer torn down mid-frame */ }
    });
  }, []);

  // ── Sprites ────────────────────────────────────────────────────
  const makeSprite = (data: PointData, id: number, world: PIXI.Container): PIXI.Sprite => {
    const s = new PIXI.Sprite(PIXI.Texture.WHITE);
    // sprite.width = 1 → scale.x = 1/tex.width; world container zoom gives final screen size.
    s.width  = 1;
    s.height = 1;
    s.tint   = parseColor(data.color);
    s.x      = data.x;
    s.y      = -data.y - (jumpOffsetsRef.current.get(id) ?? 0);
    world.addChild(s);
    return s;
  };

  const removeSprite = (id: number, world: PIXI.Container, spriteMap: Map<number, PIXI.Sprite>) => {
    const s = spriteMap.get(id);
    if (!s) return;
    world.removeChild(s);
    try { s.destroy(); } catch (_) { /* ignore */ }
    spriteMap.delete(id);
  };

  const viewRect = () => {
    const vp = viewBoundsRef.current;
    const halfW = vp.w / 2 / vp.zoom + CULL_PAD;
    const halfH = vp.h / 2 / vp.zoom + CULL_PAD;
    return { xMin: vp.x - halfW, xMax: vp.x + halfW, yMin: vp.y - halfH, yMax: vp.y + halfH };
  };

  /**
   * Bring the sprite set in line with the viewport. In sprite mode that is
   * every point in view — additions come from the spatial index, so only
   * buckets in view are touched. In bitmap mode the texture draws the world and
   * only the overlay points need a sprite.
   */
  const reconcileSprites = useCallback(() => {
    const world = worldRef.current;
    if (!world) return;
    const { xMin, xMax, yMin, yMax } = viewRect();
    const sprites = spriteMapRef.current;
    const dataMap = dataMapRef.current;
    const inView  = (d: PointData) => d.x >= xMin && d.x <= xMax && d.y >= yMin && d.y <= yMax;

    sprites.forEach((_, id) => {
      const d = dataMap.get(id);
      if (!d || !inView(d)) removeSprite(id, world, sprites);
    });

    if (modeRef.current === 'bitmap') {
      overlayIdsRef.current.forEach(id => {
        if (sprites.has(id)) return;
        const d = dataMap.get(id);
        if (d && inView(d)) sprites.set(id, makeSprite(d, id, world));
      });
      return;
    }

    forEachInRect(xMin, yMin, xMax, yMax, (id, d) => {
      if (!sprites.has(id)) sprites.set(id, makeSprite(d, id, world));
    });
  }, [forEachInRect]);

  /** Keep only the overlay sprites — used when switching into bitmap mode. */
  const dropNonOverlaySprites = useCallback(() => {
    const world = worldRef.current;
    if (!world) return;
    const sprites = spriteMapRef.current;
    const overlay = overlayIdsRef.current;
    sprites.forEach((_, id) => {
      if (!overlay.has(id)) removeSprite(id, world, sprites);
    });
  }, []);

  // ── Bitmap mode ────────────────────────────────────────────────
  const disposeBitmap = useCallback(() => {
    const b = bmpRef.current;
    if (b.sprite) {
      worldRef.current?.removeChild(b.sprite);
      try { b.sprite.destroy({ texture: true, baseTexture: true }); } catch (_) { /* ignore */ }
    }
    b.sprite = null; b.base = null; b.bytes = null; b.words = null;
    b.texW = 0; b.texH = 0;
  }, []);

  const buildBitmap = useCallback(() => {
    const world = worldRef.current;
    if (!world) return;
    const vp = viewBoundsRef.current;
    const b  = bmpRef.current;

    // One texel ≈ one screen pixel; never finer than one world cell.
    let cell = Math.max(1, Math.round(1 / vp.zoom));
    let regionW = (vp.w / vp.zoom) * REGION_PAD;
    let regionH = (vp.h / vp.zoom) * REGION_PAD;
    while ((regionW / cell) * (regionH / cell) > MAX_TEXELS) cell *= 2;

    // Snap the origin to the texel lattice so texels don't shimmer while panning.
    const x0   = Math.floor((vp.x - regionW / 2) / cell) * cell;
    const yTop = Math.ceil((vp.y + regionH / 2) / cell) * cell;
    const texW = Math.ceil(regionW / cell) + 1;
    const texH = Math.ceil(regionH / cell) + 1;

    if (!b.base || b.texW !== texW || b.texH !== texH) {
      disposeBitmap();
      const bytes = new Uint8Array(texW * texH * 4);
      const base  = new PIXI.BaseTexture(
        new PIXI.BufferResource(bytes, { width: texW, height: texH }),
        { scaleMode: PIXI.SCALE_MODES.NEAREST },
      );
      const sprite = new PIXI.Sprite(new PIXI.Texture(base));
      // Behind every overlay sprite (own pixel, jumps) added to the same container.
      world.addChildAt(sprite, 0);
      b.bytes = bytes;
      b.words = new Uint32Array(bytes.buffer);
      b.base = base;
      b.sprite = sprite;
      b.texW = texW; b.texH = texH;
    }

    const words = b.words!;
    words.fill(0);

    const overlay = overlayIdsRef.current;
    const xMax = x0 + texW * cell;
    const yMin = yTop - texH * cell;
    forEachInRect(x0, yMin, xMax, yTop, (id, d) => {
      if (overlay.has(id)) return;
      const col = Math.floor((d.x - x0) / cell);
      const row = Math.floor((yTop - d.y) / cell);
      if (col < 0 || col >= texW || row < 0 || row >= texH) return;
      words[row * texW + col] = parseAbgr(d.color);
    });

    b.base!.update();

    b.cell = cell; b.x0 = x0; b.yTop = yTop; b.dirty = false;

    const sprite = b.sprite!;
    sprite.x = x0;
    sprite.y = -yTop;
    sprite.width  = texW * cell;
    sprite.height = texH * cell;
  }, [disposeBitmap, forEachInRect]);

  /** Rebuild only when the cached region no longer covers the view, or data moved. */
  const ensureBitmap = useCallback(() => {
    const b = bmpRef.current;
    const { xMin, xMax, yMin, yMax } = viewRect();
    const vp = viewBoundsRef.current;
    const covered =
      b.sprite !== null &&
      b.cell === Math.max(1, Math.round(1 / vp.zoom)) &&
      xMin >= b.x0 && xMax <= b.x0 + b.texW * b.cell &&
      yMax <= b.yTop && yMin >= b.yTop - b.texH * b.cell;
    if (!covered || b.dirty) buildBitmap();
  }, [buildBitmap]);

  // ── Mode selection + reconcile (runs once per rendered frame) ───
  const reconcile = useCallback(() => {
    const world = worldRef.current;
    if (!world) return;
    const vp = viewBoundsRef.current;
    if (vp.w === 0) return;

    world.scale.set(vp.zoom);
    world.position.set(vp.w / 2 - vp.x * vp.zoom, vp.h / 2 + vp.y * vp.zoom);

    const { xMin, xMax, yMin, yMax } = viewRect();
    const n = countInRect(xMin, yMin, xMax, yMax);
    const mode = modeRef.current === 'bitmap'
      ? (n < LOD_LEAVE ? 'sprite' : 'bitmap')
      : (n > LOD_ENTER ? 'bitmap' : 'sprite');

    if (mode !== modeRef.current) {
      modeRef.current = mode;
      if (mode === 'bitmap') dropNonOverlaySprites();
      else disposeBitmap();
    }

    if (mode === 'bitmap') ensureBitmap();
    reconcileSprites();
  }, [countInRect, dropNonOverlaySprites, disposeBitmap, ensureBitmap, reconcileSprites]);
  reconcileRef.current = reconcile;

  /** In bitmap mode a point that must be drawn as a sprite is cut out of the texture. */
  const addOverlay = useCallback((id: number) => {
    if (overlayIdsRef.current.has(id)) return;
    overlayIdsRef.current.add(id);
    bmpRef.current.dirty = true;
    scheduleFrame();
  }, [scheduleFrame]);

  const removeOverlay = useCallback((id: number) => {
    if (!overlayIdsRef.current.delete(id)) return;
    bmpRef.current.dirty = true;
    if (modeRef.current === 'bitmap') {
      const world = worldRef.current;
      if (world) removeSprite(id, world, spriteMapRef.current);
    }
    scheduleFrame();
  }, [scheduleFrame]);

  // ── One-time PIXI initialization ──────────────────────────────
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const app = new PIXI.Application({
      width,
      height,
      backgroundColor: 0x000000,
      antialias      : false,
      resolution     : window.devicePixelRatio || 1,
      autoDensity    : true,
      // Frames are driven by scheduleFrame(), not by a free-running ticker:
      // a world that isn't changing must not cost a redraw 60 times a second.
      autoStart      : false,
      // Enabled only where we need to read back pixels each frame (e.g. the
      // history timelapse recorder blits this canvas into a 2D output canvas).
      preserveDrawingBuffer: preserveRef.current,
    });
    app.ticker.stop();

    const canvas = (app as any).view ?? (app as any).canvas as HTMLCanvasElement;
    canvas.style.display = 'block';
    el.appendChild(canvas);
    canvasElRef.current = canvas;

    const world = new PIXI.Container();
    app.stage.addChild(world);

    appRef.current  = app;
    worldRef.current = world;
    scheduleFrame();

    return () => {
      if (animRafRef.current !== null) {
        cancelAnimationFrame(animRafRef.current);
        animRafRef.current = null;
      }
      if (frameRafRef.current !== null) {
        cancelAnimationFrame(frameRafRef.current);
        frameRafRef.current = null;
      }
      jumpAnimsRef.current.clear();
      jumpOffsetsRef.current.clear();
      disposeBitmap();
      try { app.destroy(true, { children: true }); } catch (_) { /* ignore teardown errors */ }
      appRef.current  = null;
      worldRef.current = null;
      spriteMapRef.current.clear();
      dataMapRef.current.clear();
      gridRef.current.clear();
      overlayIdsRef.current.clear();
      modeRef.current = 'sprite';
    };
  }, []);

  // ── Renderer resize ────────────────────────────────────────────
  useEffect(() => {
    try { appRef.current?.renderer.resize(width, height); } catch (_) { /* not ready */ }
  }, [width, height]);

  // ── Sync point data ────────────────────────────────────────────
  useEffect(() => {
    const world = worldRef.current;
    if (!world) return;
    const t0      = performance.now();
    const sprites = spriteMapRef.current;
    const dataMap = dataMapRef.current;
    const seen    = seenIdsRef.current;
    seen.clear();

    let changed = 0;

    for (const point of points) {
      seen.add(point.id);
      const color = point.color ?? '#ffffff';
      const prev  = dataMap.get(point.id);

      if (!prev) {
        dataMap.set(point.id, { x: point.x, y: point.y, color });
        gridAdd(point.id, point.x, point.y);
        changed++;
        continue;
      }
      if (prev.x === point.x && prev.y === point.y && prev.color === color) continue;

      gridMove(point.id, prev.x, prev.y, point.x, point.y);
      prev.x = point.x; prev.y = point.y; prev.color = color;
      changed++;

      const sprite = sprites.get(point.id);
      if (sprite) {
        const fill = parseColor(color);
        if ((sprite.tint as number) !== fill) sprite.tint = fill;
        sprite.x = point.x;
        sprite.y = -point.y - (jumpOffsetsRef.current.get(point.id) ?? 0);
      }
    }

    // Remove data + sprites for deleted points.
    if (seen.size !== dataMap.size) {
      const gone: number[] = [];
      dataMap.forEach((_, id) => { if (!seen.has(id)) gone.push(id); });
      gone.forEach(id => {
        const d = dataMap.get(id)!;
        gridDrop(id, d.x, d.y);
        removeSprite(id, world, sprites);
        dataMap.delete(id);
        overlayIdsRef.current.delete(id);
      });
      changed += gone.length;
    }

    if (changed > 0) {
      bmpRef.current.dirty = true;
      scheduleFrame();
    }

    const dt = performance.now() - t0;
    if (dt > 4) {
      // eslint-disable-next-line no-console
      console.debug(
        `[renderer] pts effect ${dt.toFixed(1)}ms  total=${points.length}` +
        `  changed=${changed}  mode=${modeRef.current}  sprites=${sprites.size}`,
      );
    }
  }, [points, scheduleFrame]);

  // ── Viewport / size changes ────────────────────────────────────
  useEffect(() => {
    zoomRef.current = viewport.zoom;
    viewBoundsRef.current = { x: viewport.x, y: viewport.y, zoom: viewport.zoom, w: width, h: height };
    scheduleFrame();
  }, [viewport, width, height, scheduleFrame]);

  // ── Jump animation ─────────────────────────────────────────────
  const runJumpFrame = useRef<() => void>(() => {});
  runJumpFrame.current = () => {
    const now     = performance.now();
    const sprites = spriteMapRef.current;
    const dataMap = dataMapRef.current;

    for (const [pointId, anim] of Array.from(jumpAnimsRef.current.entries())) {
      const t      = Math.min(1, (now - anim.startTime) / 400);
      const arc    = Math.sin(t * Math.PI);
      const sprite = sprites.get(pointId);
      const data   = dataMap.get(pointId);

      if (t >= 1) {
        jumpOffsetsRef.current.delete(pointId);
        jumpAnimsRef.current.delete(pointId);
        if (sprite && data) sprite.y = -data.y;
        removeOverlay(pointId);
      } else {
        const offset = arc * anim.amplitude;
        jumpOffsetsRef.current.set(pointId, offset);
        if (sprite && data) sprite.y = -data.y - offset;
      }
    }

    scheduleFrame();

    if (jumpAnimsRef.current.size > 0) {
      animRafRef.current = requestAnimationFrame(() => runJumpFrame.current());
    } else {
      animRafRef.current = null;
    }
  };

  const triggerJump = useCallback((pointId: number) => {
    const zoom = zoomRef.current;
    const dpr  = window.devicePixelRatio || 1;
    const screenJump     = Math.max(zoom / 2, 1 / dpr);
    const worldAmplitude = screenJump / zoom;

    // A jumping point has to be its own sprite so it can leave the grid.
    addOverlay(pointId);
    jumpAnimsRef.current.set(pointId, { startTime: performance.now(), amplitude: worldAmplitude });
    if (animRafRef.current === null) {
      animRafRef.current = requestAnimationFrame(() => runJumpFrame.current());
    }
  }, [addOverlay]);

  // Update own point position instantly (called on optimistic move, before server ack).
  // The own pixel is permanently an overlay sprite: it must never wait for a
  // bitmap rebuild, and it must not linger in the bitmap at its old cell.
  const imperativeMove = useCallback((pointId: number, x: number, y: number) => {
    const data = dataMapRef.current.get(pointId);
    if (!data) return;
    if (!overlayIdsRef.current.has(pointId)) addOverlay(pointId);
    gridMove(pointId, data.x, data.y, x, y);
    data.x = x;
    data.y = y;
    const sprite = spriteMapRef.current.get(pointId);
    if (sprite) {
      sprite.x = x;
      sprite.y = -y - (jumpOffsetsRef.current.get(pointId) ?? 0);
    }
    scheduleFrame();
  }, [addOverlay, scheduleFrame]);

  const getCanvas = useCallback(() => canvasElRef.current, []);

  return { triggerJump, imperativeMove, getCanvas };
};
